import { RECURRING_ORDER_BUMPS_ENABLED } from '@repo/lib';

import findIdsOnLoops from './findIdsOnLoops';
import getPriceBillingConfig from './getPriceBillingConfig';
import synthesizePriceFromLegacy from './synthesizePriceFromLegacy';

/**
 * Client twin of apps/api/src/pages/lib/resolveOrderBumpOffers.ts — pure
 * functions over the lean JSON the storefront payload carries
 * (`page.orderBumps` entries + `page.orderBumpProducts`), no mongoose.
 *
 * The api resolver is the WRITE-path authority; this twin only drives display.
 * Any divergence must therefore cost a rejected submission, never a wrong
 * charge — so every guard here mirrors the server's, and the two test matrices
 * mirror each other. Change them together.
 *
 * Deliberately not mirrored (server-only surface): `resolveOrderBumpEntry`'s
 * rejection reasons (the attach endpoint's 400s) and `includeDisabled` (the
 * dashboard editor's listing), both of which reach the browser through the API,
 * never through this file.
 */

const normalizeCurrency = currency => (currency ?? '').toLowerCase();

// Twin of apps/api/lib/idEq — ids arrive as strings after JSON, but a caller
// may still hold an ObjectId-like from an un-serialized source.
const idEq = (a, b) => a != null && b != null && String(a) === String(b);

// Twin of the server's resolveFeatures: blank lines are rows the seller left
// empty, and an empty list is no list.
function resolveFeatures(features) {
  const lines = (Array.isArray(features) ? features : [])
    .filter(f => typeof f?.text === 'string' && f.text.trim())
    .map(f => ({ text: f.text.trim(), icon: f.icon || undefined }));
  return lines.length ? lines : undefined;
}

// The billing identity a recurring price must share with the primary to ride
// the same subscription (spec D2). `null` for anything that cannot: a one-time
// price, or a recurring price with no interval.
function recurringIdentity(price) {
  if (!price || price.billingType !== 'recurring') return null;

  const config = getPriceBillingConfig(price);
  if (!config?.interval) return null;

  // Stripe reads an absent interval_count as 1, and stored prices are
  // inconsistent about writing it — normalize or a monthly bump misses a
  // monthly primary.
  return {
    interval: config.interval,
    intervalCount: config.intervalCount ?? 1,
    currency: normalizeCurrency(price.currency),
  };
}

const sameRecurring = (a, b) =>
  a.interval === b.interval && a.intervalCount === b.intervalCount && a.currency === b.currency;

function enabledPrices(product) {
  // TODO(M14): drop the synth branch once every product carries prices[].
  if (product.prices == null) return [synthesizePriceFromLegacy(product)];

  return product.prices.filter(price => price.enabled);
}

function resolveDefaultPrice(product) {
  if (product.prices == null) return synthesizePriceFromLegacy(product);

  const preferred = product.defaultPriceId
    ? product.prices.find(price => idEq(price._id, product.defaultPriceId))
    : undefined;
  if (preferred && preferred.enabled) return preferred;

  const byOrder = product.prices
    .filter(price => price.enabled)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return byOrder[0] ?? null;
}

// What a bump has to agree with. On a checkout that is the primary product;
// on an event it is the event itself, which prices its tickets in one currency
// and is never a subscription, so nothing recurring can ride it. Null when
// there is nothing to agree with yet: a checkout with no product.
function buildPrimaryContext(page, primaryProduct) {
  if (page?.type === 'event') {
    const currency = normalizeCurrency(page.eventDetails?.currency);
    return currency ? { currency, recurringIdentities: [] } : null;
  }

  if (!primaryProduct) return null;

  const prices = enabledPrices(primaryProduct);

  return {
    currency: normalizeCurrency(primaryProduct.currency ?? prices[0]?.currency),
    recurringIdentities: prices.map(recurringIdentity).filter(identity => identity != null),
  };
}

function resolveEntry(entry, primary, bumpProducts) {
  if (entry.enabled === false) return null;

  const product = bumpProducts.find(candidate => idEq(candidate._id, entry.productId));
  if (!product) return null;
  // Mirrors the server's `not_an_order_bump` rejection. `role` carries no
  // default, so absent means 'product'.
  if (product.role !== 'orderbump') return null;

  const price = resolveDefaultPrice(product);
  if (!price) return null;
  if (price.paymentPlan) return null;

  const currency = normalizeCurrency(price.currency ?? product.currency);
  if (!currency || currency !== primary.currency) return null;

  const identity = recurringIdentity(price);
  if (price.billingType === 'recurring') {
    if (!RECURRING_ORDER_BUMPS_ENABLED) return null;
    if (!identity) return null;
    if (!primary.recurringIdentities.some(candidate => sameRecurring(candidate, identity)))
      return null;
  }

  return {
    productId: product._id,
    product,
    price,
    billingType: price.billingType,
    interval: identity?.interval,
    intervalCount: identity?.intervalCount,
    currency,
    // A placement setting, not a product one: the same bump can be preselected
    // on one page and not another. Ticked on load, never required.
    preselected: entry.preselected === true,
    // Also a placement setting, and display-only: the server never reads it, so a
    // bump revealed by `?show_order_bumps=` submits like any other (see
    // isOrderBumpHidden).
    hidden: entry.hidden === true,
    // The price rule gating the card, evaluated at selection time — see
    // isOfferShownForSelectedPrice.
    showHideLogic: entry.showHideLogic,
    // Placement setting: with it off the card is accept-or-decline, which the
    // server pins too (assertValidOrderBumpSubmission).
    allowQuantity: entry.allowQuantity === true,
    // Display-only: the card clamps a long description behind a Show more
    // toggle. Nothing on the write path reads it.
    shortenDescription: entry.shortenDescription === true,
    title: product.title || '',
    shortDescription: product.shortDescription || undefined,
    descriptionHtml: product.descriptionHtml || undefined,
    callToActionText: product.callToActionText || undefined,
    headingText: product.headingText || undefined,
    features: resolveFeatures(product.features),
    calloutText: product.calloutText || undefined,
    calloutIcon: product.calloutText ? product.calloutIcon || undefined : undefined,
  };
}

/**
 * The offers a storefront may show, in placement order. Entries failing a
 * static guard (disabled placement, missing product, a product that is not an
 * order-bump product, no enabled price, currency ≠ the primary product's,
 * payment-plan default price, any recurring bump while
 * RECURRING_ORDER_BUMPS_ENABLED is off, recurring bump with no compatible
 * enabled primary price) are silently
 * omitted, as is every entry on a checkout page with no primary product. An
 * event page is its own primary: its currency is the ticket currency and it
 * sells nothing recurring.
 *
 * Selection-time visibility is a separate gate — see
 * `isOfferCompatibleWithSelectedPrice` and `isOfferShownForSelectedPrice`.
 */
export default function getOrderBumpOffers(page, primaryProduct, bumpProducts) {
  const entries = page?.orderBumps ?? [];
  if (!entries.length) return [];

  const primary = buildPrimaryContext(page, primaryProduct);
  if (!primary) return [];

  const products = bumpProducts ?? [];

  return entries
    .map(entry => resolveEntry(entry, primary, products))
    .filter(offer => offer != null);
}

/**
 * Selection-time visibility (spec D2): a recurring bump rides the primary's
 * subscription, so it is only offerable while the SELECTED primary price shares
 * its interval, intervalCount and currency. One-time bumps ride any selection.
 */
export function isOfferCompatibleWithSelectedPrice(offer, selectedPrice) {
  if (offer.billingType !== 'recurring') return true;
  if (!offer.interval) return false;

  const selected = recurringIdentity(selectedPrice);
  if (!selected) return false;

  return sameRecurring(selected, {
    interval: offer.interval,
    intervalCount: offer.intervalCount ?? 1,
    currency: normalizeCurrency(offer.currency),
  });
}

/**
 * The seller's own visibility rule: show the bump only while the SELECTED
 * primary price is any of the ones they picked (`IS`), or none of them
 * (`IS_NOT`).
 *
 * Distinct from `isOfferCompatibleWithSelectedPrice`, which is a billing
 * constraint the seller cannot opt out of — and judged against the RAW
 * selection, never `resolveCompatSelectedPrice`. That fallback stands a default
 * price in for an unmade choice, which is right for the billing check but wrong
 * here: a multi-price checkout opens unselected on purpose (see
 * resolveInitialSelectedPriceId), so the fallback matched `IS $10` before the
 * buyer had picked $10 and the bump vanished on their first click.
 *
 * An unmade choice is therefore just not a match, which is all the null case
 * needs: `IS` hides (nothing has been picked, so it is not that price) and
 * `IS_NOT` shows (likewise). A merchant default reaches us as a real selection
 * and a single-price product resolves its lone price, so neither waits on a
 * click.
 *
 * A rule with no price chosen yet is incomplete and hides nothing, so ticking
 * the checkbox never blanks a bump before the seller has finished the sentence.
 *
 * Display only, like `hidden`: the checkout stops rendering the card and drops
 * it from form state, but nothing on the write path reads the rule.
 */
export function isOfferShownForSelectedPrice(offer, selectedPrice) {
  const logic = offer?.showHideLogic;
  const priceIds = logic?.priceIds ?? [];
  if (!logic?.enabled || !priceIds.length) return true;

  const isSelected = priceIds.some(id => idEq(selectedPrice?._id, id));

  return logic.comparison === 'IS_NOT' ? !isSelected : isSelected;
}

/**
 * The event form of the seller's rule: show the add-on while any of the named
 * ticket types is in the basket (`IS`), or while none is (`IS_NOT`).
 *
 * `ticketGroups` is the checkout form's ticket state, keyed by group:
 * `{ [groupId]: [{ ticketTypeId, quantity }] }`. A rule naming no ticket is
 * incomplete and hides nothing, as a price rule with no price does.
 */
export function isOfferShownForTickets(offer, ticketGroups) {
  const logic = offer?.showHideLogic;
  const ticketTypeIds = logic?.ticketTypeIds ?? [];
  if (!logic?.enabled || !ticketTypeIds.length) return true;

  const selectedIds = new Set(
    Object.values(ticketGroups || {})
      .flat()
      .filter(ticket => Number(ticket?.quantity) > 0)
      .map(ticket => String(ticket.ticketTypeId)),
  );
  const isSelected = ticketTypeIds.some(id => selectedIds.has(String(id)));

  return logic.comparison === 'IS_NOT' ? !isSelected : isSelected;
}

const isOrderBumpRule = logic => logic?.subject === 'ORDER_BUMP';

/**
 * The rule against whatever the page sells: a price on a checkout, tickets on an
 * event. A rule watching other bumps passes here; it needs every offer to judge,
 * so `filterOffersByRules` decides it.
 */
export function isOfferShownForSelection(offer, { page, selectedPrice, ticketGroups }) {
  if (isOrderBumpRule(offer?.showHideLogic)) return true;

  return page?.type === 'event'
    ? isOfferShownForTickets(offer, ticketGroups)
    : isOfferShownForSelectedPrice(offer, selectedPrice);
}

/**
 * The offers every seller rule lets through, `isAvailable` included, which is how
 * a watched bump the buyer cannot take (wrong interval, sold out) stops counting.
 *
 * A bump rule follows checkIsVariantEnabled: `IS` shows while any named bump is
 * ticked, `IS_NOT` while none is, and a watched bump that is itself not shown
 * hides the bump watching it whichever the comparison. Every bump on a loop of
 * rules is hidden, even one that also names a bump outside the loop. Named bumps
 * no longer placed on the page are left out, so a rule naming only those is
 * incomplete and hides nothing.
 *
 * Loops are found once up front and each bump is then decided once, so a page
 * of bumps that all watch each other costs no more than a chain. Walking every
 * path instead grew eightfold per bump and froze the checkout at ten.
 *
 * `orderBumps` is the checkout form's bump state: `{ [productId]: quantity }`.
 */
export function filterOffersByRules(
  offers,
  { page, selectedPrice, ticketGroups, orderBumps, isAvailable = () => true },
) {
  const placedIds = new Set((page?.orderBumps ?? []).map(entry => String(entry.productId)));
  const offerById = new Map(offers.map(offer => [String(offer.productId), offer]));

  const watchedIdsFor = offer => {
    const logic = offer.showHideLogic;
    if (!isOrderBumpRule(logic) || !logic.enabled) return [];

    const productId = String(offer.productId);
    return [...new Set((logic.orderBumpProductIds ?? []).map(String))].filter(
      id => id !== productId && placedIds.has(id),
    );
  };

  const loopedIds = findIdsOnLoops([...offerById.keys()], id =>
    watchedIdsFor(offerById.get(id)).filter(watchedId => offerById.has(watchedId)),
  );

  const shownById = new Map();

  const decide = (productId, offer) => {
    if (loopedIds.has(productId) || !isAvailable(offer)) return false;

    const logic = offer.showHideLogic;
    if (!isOrderBumpRule(logic)) {
      return isOfferShownForSelection(offer, { page, selectedPrice, ticketGroups });
    }

    const watchedIds = watchedIdsFor(offer);
    if (!watchedIds.length) return true;

    // Nothing off a loop leads back to itself, so this recursion always ends.
    const shownIds = watchedIds.filter(id => offerById.has(id) && isShown(id));
    if (!shownIds.length) return false;

    const isSelected = shownIds.some(id => Number(orderBumps?.[id]) > 0);

    return logic.comparison === 'IS_NOT' ? !isSelected : isSelected;
  };

  const isShown = productId => {
    if (!shownById.has(productId)) {
      shownById.set(productId, decide(productId, offerById.get(productId)));
    }
    return shownById.get(productId);
  };

  return offers.filter(offer => isShown(String(offer.productId)));
}

/**
 * The price a compatibility check should judge a recurring offer against.
 *
 * `useSelectedPrice` returns null for a legacy product with no `prices[]`
 * (callers fall back to the product scalars) and for a multi-price product the
 * buyer has not chosen from yet — and `isOfferCompatibleWithSelectedPrice`
 * fail-closes on null. Left alone, that blanks every recurring bump on exactly
 * the legacy-primary population whose submission the server would accept, since
 * the server resolves the same default price this helper does
 * (`resolveSelectedPrice(product, {})`, the rule `buildPrimaryContext` uses).
 *
 * Null still means incompatible when the product itself resolves no price —
 * nothing is being sold, so nothing can ride along.
 */
export function resolveCompatSelectedPrice(primaryProduct, selectedPrice) {
  if (selectedPrice) return selectedPrice;
  if (!primaryProduct) return null;

  return resolveDefaultPrice(primaryProduct);
}

export { getOrderBumpOffers };
